import { useSlice } from "@bridge-applications/provider-kit";
import { useEffect, type RefObject } from "react";
import { usePostHog } from "@posthog/react";
import { startupForOwner } from "../helpers/appStartup";
import { captureClientAnalytics } from "../helpers/analytics";
import { POSTHOG_ENABLED } from "../../state/config";

/** Critical visible image settlement, authoritative data, and a paint opportunity. */
export function useInitialSurfaceReady(
    root: RefObject<HTMLElement | null>,
    usable: boolean,
): void {
    const { state: auth } = useSlice("authSlice");
    const { state: startup, setSlice } = useSlice("appStartupSlice");
    const posthog = usePostHog();
    useEffect(() => {
        if (
            !usable ||
            !root.current ||
            auth.bootstrapStatus !== "ready" ||
            !auth.userId
        )
            return;
        const owner = {
            userId: auth.userId,
            generation: auth.sessionGeneration,
        };
        if (startupForOwner(startup, owner).initialSurfaceReadyAt !== null)
            return;
        let cancelled = false;
        let firstFrame = 0;
        let secondFrame = 0;
        const cleanups: Array<() => void> = [];
        const images = [...root.current.querySelectorAll("img")].filter(
            (image) => {
                const rect = image.getBoundingClientRect();
                return (
                    image.loading !== "lazy" &&
                    rect.width > 0 &&
                    rect.height > 0 &&
                    rect.top < innerHeight &&
                    rect.bottom > 0
                );
            },
        );
        const settled = images.map((image) =>
            image.complete
                ? Promise.resolve()
                : new Promise<void>((resolve) => {
                      const done = () => resolve();
                      image.addEventListener("load", done, { once: true });
                      image.addEventListener("error", done, { once: true });
                      cleanups.push(() => {
                          image.removeEventListener("load", done);
                          image.removeEventListener("error", done);
                      });
                  }),
        );
        void Promise.all(settled).then(() => {
            if (cancelled) return;
            firstFrame = requestAnimationFrame(() => {
                secondFrame = requestAnimationFrame(() => {
                    if (cancelled) return;
                    const readyAt = performance.now();
                    setSlice((previous) => ({
                        ...startupForOwner(previous, owner),
                        initialSurfaceReadyAt:
                            startupForOwner(previous, owner)
                                .initialSurfaceReadyAt ?? readyAt,
                    }));
                    if (POSTHOG_ENABLED && posthog)
                        captureClientAnalytics(
                            posthog,
                            "app_initial_surface_ready",
                            { ready_ms: readyAt, surface: "home" },
                        );
                });
            });
        });
        return () => {
            cancelled = true;
            cancelAnimationFrame(firstFrame);
            cancelAnimationFrame(secondFrame);
            cleanups.forEach((cleanup) => cleanup());
        };
    }, [
        auth.bootstrapStatus,
        auth.sessionGeneration,
        auth.userId,
        posthog,
        root,
        setSlice,
        startup,
        usable,
    ]);
}
